import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';
import { ApiError, api } from '../api/client.ts';
import { BrandPin, Icon } from '../components/Icon.tsx';
import { Field, Segmented } from '../components/ui.tsx';
import { t } from '../i18n/index.ts';
import { useStore } from '../state/store.tsx';
import { useDocumentTitle } from '../lib/documentTitle.ts';

const DEV_LOGIN = import.meta.env.DEV || import.meta.env.VITE_API_MOCK === '1';
const MOCKED = import.meta.env.VITE_API_MOCK === '1';
const PASSWORD_MIN = 12;
const PASSWORD_MAX = 128;

const safeNext = (next: string | null) => (next && next.startsWith('/') && !next.startsWith('//') ? next : '/');

type Mode = 'signIn' | 'signUp';
type FailedField = 'email' | 'password';
interface Failure {
  readonly message: string;
  readonly field?: FailedField;
}

function passwordFailure(e: unknown): Failure {
  if (!(e instanceof ApiError)) return { message: t('signin.failed') };
  const passwordIssue = e.issueFor('password');
  if (passwordIssue) return { message: passwordIssue, field: 'password' };
  if (e.code === 'invite_only') return { message: t('signin.inviteOnlyEmail'), field: 'email' };
  if (e.status === 429) return { message: t('signin.rateLimited') };
  if (e.status === 409) return { message: e.message, field: 'email' };
  if (e.status === 401) return { message: e.message, field: 'password' };
  return { message: t('signin.failed') };
}

export function SignInScreen() {
  useDocumentTitle(t('docTitle.signIn'));
  const { auth, loadSession } = useStore();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [mode, setMode] = useState<Mode>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [devEmail, setDevEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [appleAvailable, setAppleAvailable] = useState(false);
  const [signUpAvailable, setSignUpAvailable] = useState(false);
  const [paidSignup, setPaidSignup] = useState(false);
  useEffect(() => {
    api.authMethods().then(
      (m) => {
        setAppleAvailable(m.apple);
        setSignUpAvailable(m.signUp);
        setPaidSignup(m.paidSignup === true);
      },
      () => {
        setAppleAvailable(false);
        setSignUpAvailable(false);
        setPaidSignup(false);
      },
    );
  }, []);
  const [pendingPassword, setPendingPassword] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const emailId = useId();
  const passwordId = useId();
  const devEmailId = useId();
  const titleId = useId();
  const newcomerId = useId();
  const signingUp = mode === 'signUp';

  if (auth === 'signedIn') return <Navigate to={next} replace />;

  const clearErrors = () => {
    setError(null);
    setPasswordError(null);
  };

  const changeMode = (value: Mode) => {
    setMode(value);
    setShowPassword(false);
    clearErrors();
  };

  const devLogin = async (address: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.devLogin(address);
      await loadSession();
    } catch (e) {
      setError(e instanceof ApiError && e.code === 'invite_only' ? t('signin.inviteOnly') : t('signin.failed'));
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = async (event: FormEvent) => {
    event.preventDefault();
    clearErrors();
    if (signingUp && password.length < PASSWORD_MIN) return rejectPassword(t('signin.passwordTooShort'));
    if (password.length > PASSWORD_MAX) return rejectPassword(t('signin.passwordTooLong'));
    setBusy(true);
    setPendingPassword(true);
    try {
      const submit = signingUp ? api.registerWithPassword : api.loginWithPassword;
      await submit(email.trim(), password);
      await loadSession();
    } catch (e) {
      const failure = passwordFailure(e);
      if (failure.field === 'password' && signingUp) setPasswordError(failure.message);
      else setError(failure.message);
      (failure.field === 'email' ? emailRef : passwordRef).current?.focus();
    } finally {
      setBusy(false);
      setPendingPassword(false);
    }
  };

  const rejectPassword = (message: string) => {
    setPasswordError(message);
    passwordRef.current?.focus();
  };

  const apple = () => {
    if (MOCKED) void devLogin('matt@mapmymoney.app');
    else window.location.assign(api.appleStartUrl(next));
  };

  const formError = error ?? (auth === 'inviteOnly' ? t('signin.inviteOnly') : null);

  return (
    <div className="signin">
      <main className="signin__panel" id="main">
        <div className="signin__brand">
          <BrandPin size={40} />
        </div>
        <h1 className="signin__title" id={titleId}>{signingUp ? t('signin.titleSignUp') : t('signin.title')}</h1>
        <p className="signin__subtitle">{signingUp ? t('signin.subtitleSignUp') : paidSignup ? t('signin.subtitlePaid') : t('signin.subtitle')}</p>
        {signUpAvailable && (
          <Segmented
            label={t('signin.mode')}
            variant="pill"
            value={mode}
            onChange={changeMode}
            options={[
              { value: 'signIn', label: t('signin.mode.signIn') },
              { value: 'signUp', label: t('signin.mode.signUp') },
            ]}
          />
        )}
        <form className="signin__form" aria-labelledby={titleId} onSubmit={(e) => void submitPassword(e)}>
          <Field label={t('signin.email')} htmlFor={emailId}>
            <input
              ref={emailRef}
              id={emailId}
              className="input"
              type="email"
              autoComplete={signingUp ? 'email' : 'username'}
              autoCapitalize="none"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Field>
          <Field label={t('signin.password')} htmlFor={passwordId} error={passwordError} {...(signingUp ? { hint: t('signin.passwordHint') } : {})}>
            <div className="password-input">
              <input
                ref={passwordRef}
                id={passwordId}
                className="input"
                type={showPassword ? 'text' : 'password'}
                autoComplete={signingUp ? 'new-password' : 'current-password'}
                autoCapitalize="none"
                spellCheck={false}
                aria-invalid={passwordError ? true : undefined}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="password-input__toggle"
                aria-label={showPassword ? t('signin.passwordHide') : t('signin.passwordShow')}
                onClick={() => setShowPassword((shown) => !shown)}
              >
                {showPassword ? t('signin.passwordHideShort') : t('signin.passwordShowShort')}
              </button>
            </div>
          </Field>
          {signingUp && <p className="signin__note">{t('signin.passwordNote')}</p>}
          <button type="submit" className="button button--primary button--block button--lg" disabled={busy}>
            {pendingPassword
              ? signingUp ? t('signin.pendingSignUp') : t('signin.pendingSignIn')
              : signingUp ? t('signin.submitSignUp') : t('signin.submitSignIn')}
          </button>
        </form>
        {formError && <p className="signin__error" role="alert">{formError}</p>}
        {appleAvailable && (
          <>
            <p className="signin__divider" aria-hidden="true">{t('signin.or')}</p>
            <button type="button" className="button button--apple button--block button--lg" onClick={apple} disabled={busy}>
              <Icon name="apple" size={20} />
              {t('signin.apple')}
            </button>
          </>
        )}
        {paidSignup && !signingUp && (
          <>
            <section className="signin__newcomer" aria-labelledby={newcomerId}>
              <h2 className="signin__newcomer-title" id={newcomerId}>{t('signin.newcomer')}</h2>
              <Link to="/upgrade" className="button button--primary button--glow button--block button--lg">{t('signin.start')}</Link>
              <a href="/try" className="button button--outline button--block">{t('signin.try')}</a>
            </section>
            <details className="signin__help">
              <summary>{t('signin.paidNoPassword')}</summary>
              <p>{t('signin.paidNoPasswordBody')}</p>
            </details>
          </>
        )}
        {DEV_LOGIN && (
          <form className="signin__dev" aria-label={t('signin.devForm')} onSubmit={(e) => { e.preventDefault(); void devLogin(devEmail.trim()); }}>
            <p className="eyebrow">{t('signin.dev')}</p>
            <Field label={t('signin.email')} htmlFor={devEmailId}>
              <input id={devEmailId} className="input" type="email" autoComplete="off" value={devEmail} onChange={(e) => setDevEmail(e.target.value)} required />
            </Field>
            <button type="submit" className="button button--outline button--block" disabled={busy || !devEmail.includes('@')}>{t('signin.devSubmit')}</button>
          </form>
        )}
        <p className="signin__footer">{t('signin.footer')}</p>
      </main>
    </div>
  );
}
